import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MapPin, 
  Building2, 
  Briefcase, 
  Edit2, 
  UserPlus, 
  UserCheck, 
  Clock,
  MessageSquare,
  Loader2,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { userId } = useParams();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    headline: '',
    company: '',
    location: '',
    about: ''
  });

  const isOwnProfile = user?.user_id === userId;

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      setProfile(response.data);
      setEditForm({
        name: response.data.name || '',
        headline: response.data.headline || '',
        company: response.data.company || '',
        location: response.data.location || '',
        about: response.data.about || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (e) => {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/users/${userId}`, editForm, { withCredentials: true });
      setProfile(response.data);
      updateUser(response.data);
      setEditOpen(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await axios.post(`${API_URL}/api/connections?receiver_id=${userId}`, {}, { withCredentials: true });
      setProfile(prev => ({ ...prev, connection_status: 'PENDING' }));
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setConnecting(false);
    }
  };

  const handleMessage = () => {
    navigate(`/messages/${userId}`);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'SALES': return 'role-badge role-sales';
      case 'HR': return 'role-badge role-hr';
      default: return 'role-badge role-both';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <Card className="profile-header mb-6 overflow-visible" data-testid="profile-card">
          <div className="h-32 bg-gradient-to-br from-primary/20 to-secondary/50"></div>
          <CardContent className="relative pt-0 pb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-16 md:-mt-12">
              <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                <AvatarImage src={profile?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                  {getInitials(profile?.name)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 md:pb-2">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold">{profile?.name}</h1>
                  <span className={getRoleBadgeClass(profile?.role)}>
                    {profile?.role}
                  </span>
                </div>
                {profile?.headline && (
                  <p className="text-lg text-muted-foreground">{profile.headline}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                  {profile?.company && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {profile.company}
                    </span>
                  )}
                  {profile?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.location}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {isOwnProfile ? (
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn">
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Edit Profile</DialogTitle>
                        <DialogDescription>
                          Update your profile information
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditChange}
                            data-testid="edit-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="headline">Headline</Label>
                          <Input
                            id="headline"
                            name="headline"
                            placeholder="e.g., Sales Manager at Acme Inc."
                            value={editForm.headline}
                            onChange={handleEditChange}
                            data-testid="edit-headline-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">Company</Label>
                          <Input
                            id="company"
                            name="company"
                            value={editForm.company}
                            onChange={handleEditChange}
                            data-testid="edit-company-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="location">Location</Label>
                          <Input
                            id="location"
                            name="location"
                            placeholder="e.g., San Francisco, CA"
                            value={editForm.location}
                            onChange={handleEditChange}
                            data-testid="edit-location-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="about">About</Label>
                          <Textarea
                            id="about"
                            name="about"
                            placeholder="Tell us about yourself..."
                            value={editForm.about}
                            onChange={handleEditChange}
                            className="min-h-[100px]"
                            data-testid="edit-about-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="save-profile-btn"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <>
                    {profile?.connection_status === 'ACCEPTED' ? (
                      <>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={handleMessage}
                          data-testid="message-btn"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Message
                        </Button>
                        <Button variant="secondary" className="rounded-full" disabled>
                          <UserCheck className="w-4 h-4 mr-2" />
                          Connected
                        </Button>
                      </>
                    ) : profile?.connection_status === 'PENDING' ? (
                      <Button variant="secondary" className="rounded-full" disabled>
                        <Clock className="w-4 h-4 mr-2" />
                        Pending
                      </Button>
                    ) : (
                      <Button
                        onClick={handleConnect}
                        disabled={connecting}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid="connect-btn"
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Connect
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        {profile?.about && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">About</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{profile.about}</p>
            </CardContent>
          </Card>
        )}

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Professional Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Role</p>
                <p className="font-medium">{profile?.role}</p>
              </div>
              {profile?.company && (
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{profile.company}</p>
                </div>
              )}
              {profile?.location && (
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">{profile.location}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{profile?.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Member Since</p>
                <p className="font-medium">
                  {new Date(profile?.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Profile;
